/**
 * Copyright 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import styled from "styled-components";
import { Box, FlexContainer, Table, Text, theme } from "@nordcloud/gnui";
import { Resource } from "~/generated/graphql";
import { ResourceItem } from "~/components/resourceGroup/ResourceItem";
import { DuplicateResourceState } from "~/hooks/useDuplicatedResources";
import { DuplicateResourceIcon } from "./DuplicateResourceIcon";

type Props = {
  resourceGroup: {
    name: string;
    resources: Resource[];
  };
  actions?: () => JSX.Element;
  duplicateState: DuplicateResourceState;
};

export function CreateResourceGroupItem({
  resourceGroup,
  actions,
  duplicateState,
}: Props) {
  return (
    <Box innerSpacing="spacing00">
      <StyledBox padding={theme.spacing.spacing03}>
        <FlexContainer direction="row" justifyContent="flex-start">
          <Text
            tag="div"
            weight="medium"
            ml={theme.spacing.spacing03}
            css={{ wordBreak: "break-word" }}
          >
            {resourceGroup?.name}
          </Text>
          <FlexContainer
            direction="row"
            gap={theme.spacing.spacing03}
            css={{ marginLeft: "auto" }}
          >
            {actions?.()}
          </FlexContainer>
        </FlexContainer>
      </StyledBox>
      <Table>
        <Table.tbody>
          {resourceGroup.resources.map((resource, idx) => (
            <ResourceItem
              key={`${resourceGroup.name}-${idx}`}
              resource={resource}
              highlight={duplicateState.isHighlighted(resource.id)}
              actions={
                duplicateState.duplicateResourceIds?.has(resource.id)
                  ? () => (
                      <DuplicateResourceIcon
                        resourceId={resource.id}
                        onIconClick={duplicateState.onDuplicateIconClick}
                      />
                    )
                  : undefined
              }
            />
          ))}
        </Table.tbody>
      </Table>
    </Box>
  );
}

const StyledBox = styled(Box)`
  border-bottom: 2px solid ${theme.color.border.border01};
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
`;
